/**
 * useVariantDriverSync hook
 *
 * Encapsulates the logic for syncing deckline driver changes with pending KPI Variants.
 * When a driver changes on a deckline that uses a new (pending) variant:
 * 1. Updates the deckline's drivers and kpi_id
 * 2. Computes variant overrides (diff vs page_drivers)
 * 3. Updates the pending variant's overrides
 * 4. Propagates drivers + kpi_id to sibling decklines sharing the same variant
 */

import { useDeckContext } from '@context/DeckContext';
import { useDecklinesContext } from '@context/DecklinesContext';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import { usePageContext } from '@context/PageContext';
import { KPIDrivers, KPIDriversInput } from '@services/api/deckManagement/graphql/schema/API';
import { EMPTY_KPI_DRIVERS_INPUT, generateBaseKpiId } from '@utils/kpiDriverHelpers';
import { useCallback } from 'react';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { EnhancedKPIVariant } from 'types/KPILibraryContext.types';
import { DriverDisplayOptionMap } from 'types/PageContext.types';

export interface VariantDriverSyncFn {
  (
    selectedDeckline: EnhancedDeckline,
    updates: Partial<KPIDrivers>,
    updatedDriverMap: DriverDisplayOptionMap,
    enhancedVariant: EnhancedKPIVariant | null,
  ): void;
}

/**
 * Hook providing a function to sync deckline driver changes with pending variant state.
 */
const useVariantDriverSync = (): VariantDriverSyncFn => {
  const { pageDecklines, updatePageDeckline } = useDecklinesContext();
  const { rootNodes } = useDeckContext();
  const { modifiedPageProperties } = usePageContext();
  const {
    pendingCreations: { updatePendingVariant },
  } = useKPILibraryContext();

  const syncVariantDrivers: VariantDriverSyncFn = useCallback(
    (
      selectedDeckline: EnhancedDeckline,
      updates: Partial<KPIDrivers>,
      updatedDriverMap: DriverDisplayOptionMap,
      enhancedVariant: EnhancedKPIVariant | null,
    ) => {
      const newData = { ...selectedDeckline.deckline_drivers, ...updates } as KPIDrivers;
      const newKpiId = generateBaseKpiId(updatedDriverMap, rootNodes);

      // Update the selected deckline
      updatePageDeckline(selectedDeckline.metadata.internalId, 'deckline_drivers', newData);
      updatePageDeckline(selectedDeckline.metadata.internalId, 'kpi_id', newKpiId);

      // Sync pending variant overrides and sibling decklines
      if (enhancedVariant?.isNew && modifiedPageProperties?.page_drivers) {
        const pageDrivers = modifiedPageProperties.page_drivers;
        const variantId = enhancedVariant.kpi_variant_id;

        // Compute overrides as a full-field object: non-null for fields that differ from page_drivers,
        // explicit null for inherited fields. The API expects all fields present on KPIDriversInput.
        const overrides = (Object.keys(pageDrivers) as Array<keyof KPIDrivers>)
          .filter((field) => field !== '__typename')
          .reduce<KPIDriversInput>(
            (acc, field) => {
              const decklineValue = newData[field] ?? null;
              const pageValue = pageDrivers[field] ?? null;
              (acc as Record<string, unknown>)[field] = decklineValue !== pageValue ? decklineValue : null;
              return acc;
            },
            { ...EMPTY_KPI_DRIVERS_INPUT },
          );

        updatePendingVariant(variantId, { variantOverrides: overrides });

        // Propagate to sibling decklines sharing this variant
        pageDecklines
          .filter(
            (dl) => dl.kpi_variant_id === variantId && dl.metadata.internalId !== selectedDeckline.metadata.internalId,
          )
          .forEach((dl) => {
            updatePageDeckline(dl.metadata.internalId, 'deckline_drivers', newData);
            updatePageDeckline(dl.metadata.internalId, 'kpi_id', newKpiId);
          });
      }
    },
    [rootNodes, updatePageDeckline, modifiedPageProperties, updatePendingVariant, pageDecklines],
  );

  return syncVariantDrivers;
};

export default useVariantDriverSync;
